/**
 * Numa barra empilhada, só a ponta VISÍVEL (topo do segmento mais alto, base
 * do mais baixo) arredonda — não o índice de primeira-aparição do material
 * no dataset (índice fixo erra sempre que um material tem valor 0 num
 * período específico, já que a "ponta" real muda de segmento por barra).
 * Estas duas funções são puras pra dar pra testar sem montar o SVG/recharts.
 */

export interface SegmentRadii {
  topRadius: number;
  bottomRadius: number;
}

/**
 * Abaixo dessa fração do total da barra, o segmento é ignorado pra decidir
 * quem é o topo/base visual — um resíduo de arredondamento (ex.: 0,001kg
 * num total de 3.000kg) tecnicamente é "> 0", mas rende como sliver
 * invisível; se ele for o último da pilha, rouba o arredondamento do
 * segmento que está de fato visível abaixo dele.
 */
const MIN_VISIBLE_RATIO = 0.005;

/**
 * `stackOrder` é a ordem de empilhamento (primeiro = base, último = topo —
 * mesma ordem dos `<Bar>` no JSX). `payload` é a linha de dados completa
 * daquela barra (todos os materiais daquele período).
 */
export function getSegmentRadii(
  stackOrder: string[],
  payload: Record<string, number | string> | undefined,
  dataKey: string,
  radius: number,
): SegmentRadii {
  const values = stackOrder.map((id) => Number(payload?.[id]) || 0);
  const total = values.reduce((sum, value) => sum + value, 0);
  const visibleIds =
    total > 0
      ? stackOrder.filter((id, index) => values[index] / total > MIN_VISIBLE_RATIO)
      : [];
  const isBottom = visibleIds[0] === dataKey;
  const isTop = visibleIds[visibleIds.length - 1] === dataKey;
  return {
    topRadius: isTop ? radius : 0,
    bottomRadius: isBottom ? radius : 0,
  };
}

/** Path SVG de um retângulo com raio independente no topo e na base (lados retos). */
export function roundedBarPath(
  x: number,
  y: number,
  width: number,
  height: number,
  topRadius: number,
  bottomRadius: number,
): string {
  const maxRadius = Math.max(0, Math.min(width / 2, height / 2));
  const tr = Math.min(Math.max(0, topRadius), maxRadius);
  const br = Math.min(Math.max(0, bottomRadius), maxRadius);

  return `
    M ${x},${y + tr}
    Q ${x},${y} ${x + tr},${y}
    L ${x + width - tr},${y}
    Q ${x + width},${y} ${x + width},${y + tr}
    L ${x + width},${y + height - br}
    Q ${x + width},${y + height} ${x + width - br},${y + height}
    L ${x + br},${y + height}
    Q ${x},${y + height} ${x},${y + height - br}
    Z
  `.trim();
}
